import React from "react";
import {
  MapPin,
  Wrench,
  Users,
  Globe2,
  Building2,
  Compass,
  ShieldCheck,
  Link2,
  Wallet,
  Handshake,
} from "lucide-react";
import { SectionHeader, Card, PrincipleChip, ComplianceBand } from "@/components/Section";

const PARTNERS_IMG =
  "https://images.unsplash.com/photo-1758518727600-2c5f48419eac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzc4MzQwODA3fDA&ixlib=rb-4.1.0&q=85";

const Partners = () => {
  const relationships = [
    { icon: MapPin, label: "Local real estate agents" },
    { icon: Wrench, label: "Property service providers" },
    { icon: Users, label: "Rental counterparties" },
    { icon: Globe2, label: "Real estate platforms" },
    { icon: Building2, label: "Corporate partners" },
  ];

  const principles = [
    { icon: Compass, label: "Long-term orientation" },
    { icon: ShieldCheck, label: "Conservative operational model" },
    { icon: Link2, label: "Direct business relationships" },
    { icon: Wallet, label: "Internal capital only" },
    { icon: Handshake, label: "Professional counterparties" },
  ];

  return (
    <div data-testid="partners-page">
      {/* HERO */}
      <section className="bg-white">
        <div className="container-tight pt-16 md:pt-24 pb-16 md:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-7">
              <div className="overline reveal">Approach</div>
              <h1
                data-testid="partners-title"
                className="reveal reveal-delay-1 mt-6 font-display text-4xl sm:text-5xl lg:text-[60px] font-bold tracking-tight text-navy leading-[1.05]"
              >
                Partners &amp; Approach
              </h1>
              <p
                data-testid="partners-intro"
                className="reveal reveal-delay-2 mt-8 text-[18px] leading-relaxed text-charcoal max-w-2xl"
              >
                Taonga OÜ works through direct business relationships and
                professional counterparties. The company values transparency,
                operational simplicity and long-term cooperation.
              </p>
            </div>
            <div className="lg:col-span-5 reveal reveal-delay-2">
              <img
                src={PARTNERS_IMG}
                alt="Two professionals discussing in business attire"
                className="w-full h-[380px] object-cover rounded-md border border-border"
                loading="eager"
                data-testid="partners-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* PROFESSIONAL RELATIONSHIPS */}
      <section className="section bg-soft" data-testid="relationships-section">
        <div className="container-tight">
          <SectionHeader
            overline="Professional Relationships"
            title="Counterparties we work with"
            testId="relationships-header"
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {relationships.map(({ icon: Icon, label }, i) => (
              <Card
                key={label}
                icon={Icon}
                title={label}
                testId={`relationship-card-${i}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* OPERATING PRINCIPLES */}
      <section className="section bg-white" data-testid="op-principles-section">
        <div className="container-tight">
          <SectionHeader
            overline="Operating Principles"
            title="The way we work"
            testId="op-principles-header"
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {principles.map(({ icon: Icon, label }, i) => (
              <div
                key={label}
                data-testid={`op-principle-${i}`}
                className="hover-lift bg-white border border-border rounded-md p-7 flex items-start gap-4"
              >
                <Icon className="h-5 w-5 text-warm mt-0.5 shrink-0" strokeWidth={1.5} />
                <div className="font-display text-[16px] font-semibold text-navy leading-snug">
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORM RELATIONSHIPS */}
      <section className="bg-soft border-t border-border" data-testid="platform-relationships-section">
        <div className="container-tight py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="overline">Platform Relationships</div>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy leading-[1.1]">
                European property platforms
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-[17px] leading-relaxed text-charcoal max-w-2xl">
                The company may use established European property and
                crowdfunding platforms, including Estateguru and Crowdestate,
                as part of its broader property-related activities and market
                evaluation process.
              </p>
              <ul className="mt-6 flex flex-wrap gap-3">
                {["Estateguru", "Crowdestate"].map((p) => (
                  <li
                    key={p}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-md bg-white text-[14px] text-navy font-medium"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-warm" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* OWN-CAPITAL MODEL */}
      <section className="bg-white border-t border-border" data-testid="own-capital-section">
        <div className="container-tight py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="overline">Own-Capital Model</div>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy leading-[1.1]">
                Own corporate capital only
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-[17px] leading-relaxed text-charcoal max-w-2xl">
                Taonga OÜ operates using its own corporate capital and does not
                accept public deposits or manage third-party client assets.
              </p>
            </div>
          </div>
        </div>
      </section>

      <ComplianceBand testId="partners-compliance-band">
        Taonga OÜ does not solicit or accept third-party funds and does not
        provide regulated financial services, brokerage or asset management.
      </ComplianceBand>
    </div>
  );
};

export default Partners;
